/* eslint-disable */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { PLAN_ORDER } from "@/lib/pricing";
import {
  Check, X, Sparkles, Shield, Star, ChevronDown, Crown, CreditCard, Globe,
  Minus, Loader2, Zap,
} from "lucide-react";

const TRUST_ICONS = [Shield, X, CreditCard, Globe];
const eur = (v) => Number(v).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function Cell({ v }) {
  if (v === true) return <Check size={16} className="text-[#22C55E] mx-auto" />;
  if (v === false) return <Minus size={16} className="text-[#3A4A63] mx-auto" />;
  if (v === "Add-on") return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FBBF24]/15 text-[#FBBF24]">Add-on</span>;
  return <span className="font-mono-plex text-xs text-[#E2E8F0]">{v}</span>;
}

export default function PricingSection({
  plans: plansProp,
  addons: addonsProp,
  user = null,
  currentPlan = null,
  busyId = null,
  onSelectPlan,
  onSelectAddon,
}) {
  const { t } = useTranslation();
  const [plans, setPlans] = useState(plansProp || null);
  const [addons, setAddons] = useState(addonsProp || null);
  const [annual, setAnnual] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    if (plansProp) { setPlans(plansProp); setAddons(addonsProp); return; }
    api.get("/plans").then((r) => { setPlans(r.data.plans); setAddons(r.data.addons); }).catch(() => {});
  }, [plansProp, addonsProp]);

  if (!plans) return <div className="text-sm text-[#94A3B8] py-10 text-center">{t("pricing.loading")}</div>;
  const addon = addons?.ai_project_manager;
  const comparison = t("pricing.comparison", { returnObjects: true }) || [];
  const trust = t("pricing.trust", { returnObjects: true }) || [];
  const testimonials = t("pricing.testimonials", { returnObjects: true }) || [];
  const faqs = t("pricing.faqs", { returnObjects: true }) || [];

  return (
    <div data-testid="pricing-section">
      {/* Billing toggle */}
      <div className="flex items-center justify-center gap-3 mb-10" data-testid="billing-toggle">
        <span className={`text-sm font-semibold ${!annual ? "text-[#E2E8F0]" : "text-[#94A3B8]"}`}>{t("pricing.monthly")}</span>
        <button onClick={() => setAnnual((a) => !a)} data-testid="billing-toggle-btn"
          className={`relative w-14 h-8 rounded-full transition-colors ${annual ? "bg-[#00BFFF]" : "bg-[#1E293B]"}`}>
          <span className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white transition-transform ${annual ? "translate-x-6" : "translate-x-0"}`} />
        </button>
        <span className={`text-sm font-semibold ${annual ? "text-[#E2E8F0]" : "text-[#94A3B8]"}`}>{t("pricing.annual")}</span>
        <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-[#22C55E]/15 text-[#22C55E]">{t("pricing.save20")}</span>
      </div>

      {/* Plan cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 items-start">
        {PLAN_ORDER.map((pid) => {
          const p = plans[pid];
          if (!p) return null;
          const featured = pid === "pro";
          const isCurrent = currentPlan === pid;
          const price = annual ? p.price_annual : p.price;
          const perMonth = annual && p.price_annual ? p.price_annual / 12 : p.price;
          const target = t(`pricing.plans.${pid}.target`, { defaultValue: p.target || "" });
          const cta = t(`pricing.plans.${pid}.cta`, { defaultValue: p.cta || "" });
          const badge = t(`pricing.plans.${pid}.badge`, { defaultValue: "" });
          const highlight = p.highlight ? t(`pricing.plans.${pid}.highlight`, { defaultValue: p.highlight }) : "";
          const features = t(`pricing.plans.${pid}.features`, { returnObjects: true, defaultValue: p.features || [] });
          return (
            <div key={pid} data-testid={`plan-card-${pid}`}
              className={`relative rounded-xl border p-6 flex flex-col transition-transform ${
                featured ? "border-[#00BFFF] bg-[#0E1726] lg:-mt-3 lg:mb-3 shadow-[0_0_50px_-12px_rgba(0,191,255,0.45)]" : "border-[#1E293B] bg-[#0B111D]"
              }`}>
              {highlight && (
                <div className={`absolute -top-3 left-6 text-[11px] font-bold px-3 py-1 rounded-full ${featured ? "bg-[#00BFFF] text-[#03070E]" : "bg-[#1E293B] text-[#94A3B8]"}`} data-testid={`badge-${pid}`}>
                  {featured && <Crown size={11} className="inline mr-1 -mt-0.5" />}{highlight}
                </div>
              )}
              <div className="flex items-center justify-between">
                <div className="font-display text-xl font-extrabold">{p.name}</div>
                {badge && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FBBF24]/15 text-[#FBBF24]">{badge}</span>}
              </div>
              <p className="text-xs text-[#94A3B8] mt-1 min-h-[32px]">{target}</p>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-display text-4xl font-extrabold tracking-tighter">{pid === "free" ? "0" : eur(price)}</span>
                <span className="text-sm text-[#94A3B8]">€{pid === "free" ? t("pricing.perMonth") : annual ? t("pricing.perYear") : t("pricing.perMonth")}</span>
              </div>
              {pid !== "free" && annual && (
                <div className="text-xs text-[#22C55E] mt-1">{t("pricing.approxMonth", { v: eur(perMonth) })}</div>
              )}
              {pid === "business" && p.extra_user_price && (
                <div className="text-[11px] text-[#94A3B8] mt-1">{t("pricing.extraUser", { v: eur(p.extra_user_price) })}</div>
              )}

              {isCurrent ? (
                <div className="mt-5 w-full text-center py-3 text-sm font-semibold rounded-lg border border-[#22C55E] text-[#22C55E]" data-testid={`current-${pid}`}>
                  <Check size={14} className="inline mr-1" /> {t("pricing.currentPlan")}
                </div>
              ) : (
                <button onClick={() => onSelectPlan && onSelectPlan(pid, annual ? "annual" : "monthly")}
                  disabled={busyId === pid}
                  data-testid={`select-plan-${pid}`}
                  className={`mt-5 w-full py-3 rounded-lg font-bold text-sm transition-colors disabled:opacity-50 ${
                    featured ? "bg-[#00BFFF] text-[#03070E] hover:bg-[#38CFFF]" : "bg-[#111C30] text-[#E2E8F0] hover:bg-[#1E293B] border border-[#1E293B]"
                  }`}>
                  {busyId === pid ? <Loader2 size={16} className="animate-spin mx-auto" /> : cta}
                </button>
              )}

              <ul className="mt-6 space-y-2.5 text-sm">
                {(Array.isArray(features) ? features : []).map((f, i) => (
                  <li key={i} className="flex gap-2 items-start">
                    <Check size={15} className={`mt-0.5 shrink-0 ${featured ? "text-[#00BFFF]" : "text-[#22C55E]"}`} />
                    <span className="text-[#CBD5E1]">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {/* AI Project Manager add-on */}
      {addon && (
        <div className="mt-6 rounded-xl border border-[#00BFFF]/30 bg-gradient-to-r from-[#0E1726] to-[#0B111D] p-6 flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid="addon-card">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-[#00BFFF]/10 flex items-center justify-center shrink-0">
              <Sparkles size={22} className="text-[#00BFFF]" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-display text-lg font-bold">{t("pricing.addon.name", { defaultValue: addon.name })}</h3>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FBBF24]/15 text-[#FBBF24]">{t("pricing.addonBadge")}</span>
              </div>
              <p className="text-sm text-[#94A3B8] mt-1 max-w-xl">{t("pricing.addon.description", { defaultValue: addon.description })}</p>
              <p className="text-xs text-[#22C55E] mt-1">{t("pricing.addonIncluded")}</p>
            </div>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <div className="text-right">
              <div className="font-display text-2xl font-extrabold">{eur(annual ? addon.price_annual : addon.price)} €</div>
              <div className="text-xs text-[#94A3B8]">{annual ? t("pricing.perYear") : t("pricing.perMonth")}</div>
            </div>
            <button onClick={() => onSelectAddon && onSelectAddon(addon.id, annual ? "annual" : "monthly")}
              disabled={busyId === addon.id}
              data-testid="select-addon"
              className="px-5 py-3 rounded-lg font-bold text-sm bg-[#00BFFF] text-[#03070E] hover:bg-[#38CFFF] transition-colors disabled:opacity-50">
              {busyId === addon.id ? <Loader2 size={16} className="animate-spin" /> : t("pricing.addToBasic")}
            </button>
          </div>
        </div>
      )}

      {/* Trust indicators */}
      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="trust-indicators">
        {(Array.isArray(trust) ? trust : []).map((text, i) => {
          const Icon = TRUST_ICONS[i] || Shield;
          return (
            <div key={i} className="flex items-center gap-2.5 text-sm text-[#94A3B8] card-flat px-4 py-3">
              <Icon size={16} className="text-[#00BFFF] shrink-0" /> {text}
            </div>
          );
        })}
      </div>

      {/* Comparison table */}
      <div className="mt-16" data-testid="comparison-table">
        <h3 className="font-display text-2xl font-extrabold text-center">{t("pricing.compareTitle")}</h3>
        <div className="mt-8 overflow-x-auto card-flat">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="border-b border-[#1E293B]">
                <th className="text-left p-4 font-display font-bold">{t("pricing.featureCol")}</th>
                {PLAN_ORDER.map((pid) => (
                  <th key={pid} className={`p-4 font-display font-bold text-center ${pid === "pro" ? "text-[#00BFFF]" : ""}`}>{plans[pid]?.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(Array.isArray(comparison) ? comparison : []).map((row, i) => (
                <tr key={i} className="border-b border-[#1E293B]/60 hover:bg-[#111C30]/40">
                  <td className="p-4 text-[#CBD5E1]">{row.label}</td>
                  {PLAN_ORDER.map((pid) => (
                    <td key={pid} className={`p-4 text-center ${pid === "pro" ? "bg-[#00BFFF]/[0.04]" : ""}`}><Cell v={row.values[pid]} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Testimonials */}
      <div className="mt-16" data-testid="testimonials">
        <h3 className="font-display text-2xl font-extrabold text-center">{t("pricing.testimonialsTitle")}</h3>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
          {(Array.isArray(testimonials) ? testimonials : []).map((tm, i) => (
            <div key={i} className="card-flat p-6">
              <div className="flex gap-0.5 text-[#FBBF24]">{[...Array(5)].map((_, j) => <Star key={j} size={14} fill="currentColor" />)}</div>
              <p className="text-sm text-[#CBD5E1] mt-3 leading-relaxed">"{tm.quote}"</p>
              <div className="mt-4">
                <div className="font-semibold text-sm">{tm.name}</div>
                <div className="text-xs text-[#94A3B8]">{tm.role}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ */}
      <div className="mt-16 max-w-3xl mx-auto" data-testid="faq">
        <h3 className="font-display text-2xl font-extrabold text-center">{t("pricing.faqTitle")}</h3>
        <div className="mt-8 space-y-3">
          {(Array.isArray(faqs) ? faqs : []).map((f, i) => (
            <div key={i} className="card-flat overflow-hidden" data-testid={`faq-${i}`}>
              <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} aria-expanded={openFaq === i} className="w-full flex items-center justify-between gap-4 p-4 text-left">
                <span className="font-semibold text-sm">{f.q}</span>
                <ChevronDown size={18} className={`text-[#94A3B8] shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
              </button>
              {openFaq === i && <div className="px-4 pb-4 text-sm text-[#94A3B8] leading-relaxed">{f.a}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
